import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import classnames from 'classnames';
import { Button } from 'react-style-guide';
import { TranslateFunction } from 'react-utilities';
import { Thumbnail2d, ThumbnailTypes, ThumbnailUniverseThumbnailSize } from 'roblox-thumbnails';
import { TAssetType, TCarouselItem } from '../types/carouselTypes';
import YouTubePlayer, { TYoutubePlayerRef } from './YouTubePlayer';
import carouselConstants from '../constants/carouselConstants';

export type TCarouselRef = {
  start: () => void;
  stop: () => void;
  next: () => void;
  back: () => void;
};

const Carousel = (
  {
    items,
    translate
  }: {
    items: TCarouselItem[];
    translate: TranslateFunction;
  },
  ref: React.Ref<TCarouselRef>
): JSX.Element => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [showControls, setShowControls] = useState<boolean>(false);
  const showControlsRef = useRef<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const selectedIndexRef = useRef<number>(selectedIndex);
  const youtubeVideoRefs = useRef<Record<number, TYoutubePlayerRef>>({});

  const stopAllVideo = () => {
    if (youtubeVideoRefs.current) {
      Object.values(youtubeVideoRefs.current).forEach(youtubeVideoRef => {
        youtubeVideoRef.pause();
      });
    }
  };

  const next = () => {
    stopAllVideo();
    const nextIndex = (selectedIndexRef.current + 1) % items.length;
    setSelectedIndex(nextIndex);
    selectedIndexRef.current = nextIndex;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    start();
  };

  const back = () => {
    stopAllVideo();
    const nextIndex =
      selectedIndexRef.current - 1 < 0 ? items.length - 1 : selectedIndexRef.current - 1;
    setSelectedIndex(nextIndex);
    selectedIndexRef.current = nextIndex;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    start();
  };

  const stop = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const start = () => {
    stop();
    // NOTE (07/22/21, jcountrymam): Carousel should not
    // transition when video is playing and controls are not shown
    if (!isVideoPlaying && !showControlsRef.current) {
      timeoutRef.current = setTimeout(() => {
        next();
      }, carouselConstants.carouselConfigs.delay);
    }
  };

  useEffect(() => {
    start();
    document
      .getElementById(carouselConstants.gameplayButtonContainerId)
      ?.addEventListener('click', stopAllVideo);

    return () => {
      stop();
      document
        .getElementById(carouselConstants.gameplayButtonContainerId)
        ?.removeEventListener('click', stopAllVideo);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    start,
    stop,
    next,
    back
  }));

  return (
    <div
      onMouseEnter={() => {
        setShowControls(true);
        showControlsRef.current = true;
        stop();
      }}
      onMouseLeave={() => {
        setShowControls(false);
        showControlsRef.current = false;
        start();
      }}>
      {showControls && items.length > 1 && (
        <React.Fragment>
          <Button
            className='carousel-controls carousel-controls-right'
            onClick={next}
            aria-label={translate(carouselConstants.carouselTranslationMap.next)}>
            <span className='icon-carousel-right' />
          </Button>

          <Button
            className='carousel-controls carousel-controls-left'
            onClick={back}
            aria-label={translate(carouselConstants.carouselTranslationMap.back)}>
            <span className='icon-carousel-left' />
          </Button>
        </React.Fragment>
      )}
      {items.map((item, index) => {
        switch (item.type) {
          case TAssetType.Image:
            return (
              <Thumbnail2d
                key={item.assetId}
                type={ThumbnailTypes.assetThumbnail}
                size={ThumbnailUniverseThumbnailSize.width768}
                targetId={item.assetId}
                containerClass={classnames('carousel-item', {
                  'carousel-item-active': index === selectedIndex,
                  'carousel-item-active-out':
                    index === (selectedIndex - 1 < 0 ? items.length - 1 : selectedIndex - 1) ||
                    index === (selectedIndexRef.current + 1) % items.length
                })}
              />
            );
          case TAssetType.YouTubeVideo:
            return (
              <YouTubePlayer
                ref={youTubePlayerRef => {
                  if (youTubePlayerRef) {
                    youtubeVideoRefs.current[index] = youTubePlayerRef;
                  }
                }}
                id={item.assetId}
                key={item.assetId}
                className={classnames('carousel-item', 'carousel-video', {
                  'carousel-item-active': index === selectedIndex,
                  'carousel-item-active-out':
                    index === (selectedIndex - 1 < 0 ? items.length - 1 : selectedIndex - 1) ||
                    index === (selectedIndexRef.current + 1) % items.length
                })}
                onPlay={() => {
                  stop();
                  setIsVideoPlaying(true);
                }}
                onPaused={() => {
                  start();
                  setIsVideoPlaying(false);
                }}
                onEnd={() => {
                  start();
                  setIsVideoPlaying(false);
                }}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default forwardRef(Carousel);
